import * as THREE from 'three';

// Loads public/textures/crowd-atlas.{png,json} once, at application startup,
// and hands the resolved texture to whoever asks. Built offline by
// `npm run build:crowd` (tools/build-crowd-atlas.mjs) - nothing here does any
// image processing.
//
// RELATIVE urls on purpose, unlike every /api/* call which goes through
// API_BASE: express.static sends no Access-Control-Allow-Origin, so an
// absolute cross-origin fetch would be blocked outright when this page runs
// from the Tizen app's own file:// origin. A relative path resolves correctly
// in both places - against the Pi when it serves party-mode.html, and against
// the app's own directory when the TV bundle carries its own copy.
const ATLAS_PNG = 'textures/crowd-atlas.png';
const ATLAS_JSON = 'textures/crowd-atlas.json';

let _atlas = null;
let _promise = null;

function configure(texture) {
  texture.colorSpace = THREE.SRGBColorSpace;
  // The atlas is stored straight (RGB is a flat white under both opaque and
  // transparent texels), so premultiplying here would be wrong twice over.
  texture.premultiplyAlpha = false;
  // The far row is ~35px tall, so it lands several mip levels down; without a
  // mip chain it would alias into sparkling noise on every camera drift.
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 1;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

// Resolves to { texture, meta } or to null. Never rejects: a missing atlas is
// a degraded visualizer, not a broken page.
export function preloadCrowdAtlas() {
  if (_promise) return _promise;
  _promise = Promise.all([
    fetch(ATLAS_JSON).then(function (r) {
      if (!r.ok) throw new Error('crowd-atlas.json HTTP ' + r.status);
      return r.json();
    }),
    new Promise(function (resolve, reject) {
      new THREE.TextureLoader().load(ATLAS_PNG, resolve, undefined, function () {
        reject(new Error('crowd-atlas.png failed to load'));
      });
    })
  ]).then(function (parts) {
    _atlas = { texture: configure(parts[1]), meta: parts[0] };
    return _atlas;
  }).catch(function (err) {
    console.error('[party-mode] crowd atlas unavailable, MainStage will render without the crowd', err);
    _atlas = null;
    return null;
  });
  return _promise;
}

// Synchronous accessor for construction sites that cannot await (the switcher's
// factories, adaptive-party's lazy mode swap). Returns null until the preload
// above has resolved.
export function getCrowdAtlas() {
  return _atlas;
}

// The atlas is shared and preloaded: whoever uses it must NOT dispose it.
// Exposed only so the leak test can account for the one texture it adds.
export function isCrowdAtlasLoaded() {
  return _atlas !== null;
}
